import React from 'react';
import { TextField, Paper, CircularProgress, Chip } from '@mui/material';
import { Button, Typography } from 'components/index';
import { Edit as EditIcon, Delete } from '@mui/icons-material';
import { NoteDTO } from 'common/utils/util.type';
import { joinDateAndUser } from 'common/utils/common.util';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WhiteFab from '../../common/WhiteFab';
import { ReportStatusEnum } from '../../report/report.type';
import theme from 'assets/theme';

interface DisplayNoteProps {
  note: NoteDTO;
  index: number;
  editingNoteId: string | null | undefined;
  isLocked: boolean | undefined;
  tooltips: { unlockToEdit: string; reportStatus: string } | undefined;
  setEditingNoteId: (id: string | null) => void;
  handleEditClick: (note: NoteDTO) => void;
  handleDeleteNote: (noteId: string) => void;
  handleUpdateNote: (noteId: string) => void;
  editedContent: string;
  setEditedContent: (content: string) => void;
  updatingNote: boolean;
  deletingNote: boolean;
  reportStatus?: string;
  selectedNoteForDeletion: string | null;
}

export const DisplayNote: React.FC<DisplayNoteProps> = ({
  note,
  index,
  editingNoteId,
  isLocked,
  tooltips,
  setEditingNoteId,
  handleEditClick,
  handleDeleteNote,
  handleUpdateNote,
  editedContent,
  setEditedContent,
  updatingNote,
  deletingNote,
  reportStatus,
  selectedNoteForDeletion,
}) => {
  return (
    <Paper
      key={note.id}
      style={{
        marginTop: '10px',
        padding: '15px',
        backgroundColor: note.isAutoGenerated ? '#e3f2fd' : '#f7f7f7', // Light blue for autogenerated notes
        position: 'relative',
        borderRadius: '5px',
      }}
    >
      <div
        style={{
          minHeight: '50px',
          wordWrap: 'break-word',
          maxWidth: editingNoteId === note.id || note.isPayappNote ? '100%' : '95%',
          whiteSpace: 'pre-wrap',
        }}
      >
        {editingNoteId === note.id ? (
          <>
            <TextField
              value={editedContent}
              multiline
              rows={4}
              onChange={e => {
                const inputValue = e.target.value;
                if (inputValue.trim().length > 0 || inputValue === '') {
                  setEditedContent(inputValue);
                }
              }}
              fullWidth
              variant='outlined'
              margin='normal'
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <Button
                variant='outlined'
                color='error'
                onClick={() => setEditingNoteId(null)}
                style={{ marginRight: '10px' }}
                disabled={isLocked || updatingNote}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleUpdateNote(note.id || '')}
                loading={updatingNote}
                disabled={isLocked || updatingNote || editedContent.trim().length === 0 || editedContent === note.content}
              >
                Update
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography variant='subtitle2' fontSize='small'>
              {joinDateAndUser(note.createdOn, note.createdBy)}
              {note.isPayappNote && (
                <Chip
                  label='PayApp'
                  size='small'
                  style={{
                    marginLeft: '8px',
                    height: '18px',
                    fontSize: '0.7rem',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  }}
                />
              )}
              {note.isAutoGenerated && (
                <Chip
                  label='Auto'
                  size='small'
                  style={{
                    marginLeft: '8px',
                    height: '18px',
                    fontSize: '0.7rem',
                    backgroundColor: theme.palette.info.main,
                    color: 'white',
                  }}
                />
              )}
              {note.updatedBy && note.createdBy && (
                <span>
                  {` (Edited`}
                  <Tooltip title={`Edited: ${joinDateAndUser(note.updatedOn, note.updatedBy)}`}>
                    <InfoOutlinedIcon
                      fontSize='inherit'
                      style={{ marginLeft: '4px', cursor: 'pointer', fontSize: '1rem', paddingBottom: '2px' }}
                    />
                  </Tooltip>
                  {`)`}
                </span>
              )}
            </Typography>
            <Typography
              variant='body1'
              fontSize='0.8em'
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                width: '100%',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {note.content}
            </Typography>
            {!note.isPayappNote && (
              <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                <Tooltip
                  title={
                    isLocked && reportStatus === ReportStatusEnum.Completed
                      ? tooltips?.reportStatus
                      : isLocked
                        ? tooltips?.unlockToEdit
                        : 'Edit note'
                  }
                  arrow={false}
                >
                  <span>
                    <WhiteFab
                      aria-label='edit'
                      icon={<EditIcon fontSize='small' />}
                      textColor='primary'
                      onClick={() => handleEditClick(note)}
                      size='small'
                      disabled={isLocked || deletingNote}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    isLocked && reportStatus === ReportStatusEnum.Completed
                      ? tooltips?.reportStatus
                      : isLocked
                        ? tooltips?.unlockToEdit
                        : 'Delete note'
                  }
                  arrow={false}
                >
                  <span>
                    <WhiteFab
                      aria-label='delete'
                      icon={
                        selectedNoteForDeletion === note?.id ? <CircularProgress size={24} color='error' /> : <Delete fontSize='small' />
                      }
                      textColor='error'
                      onClick={() => (note?.id ? handleDeleteNote(note.id) : null)}
                      size='small'
                      sx={{ ml: 1 }}
                      disabled={isLocked}
                    />
                  </span>
                </Tooltip>
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  );
};
